import { Phone, Mail, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/content";

export default function Contact() {
  const { t } = useSite();
  const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Anfrage Immobilienbetreuung Kern")}`;

  const rows = [
    { icon: Phone, label: t.contact.phone, value: CONTACT.phone, href: CONTACT.phoneHref, tid: "contact-phone" },
    { icon: Mail, label: t.contact.email, value: CONTACT.email, href: mailto, tid: "contact-email" },
    { icon: Clock, label: t.contact.hours, value: `${t.contact.h1} · ${t.contact.h2}`, tid: "contact-hours" },
    { icon: MapPin, label: t.contact.address, value: CONTACT.address, href: CONTACT.maps, external: true, tid: "contact-address" },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-ibk" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-ibk">{t.contact.label}</span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.05]">
                {t.contact.title}
              </h2>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={mailto}
                  data-testid="contact-cta-email"
                  className="group inline-flex items-center gap-2 rounded-full bg-ibk px-7 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  {t.contact.cta}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={CONTACT.maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-cta-route"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-semibold text-foreground hover:border-ibk hover:text-ibk transition-colors"
                >
                  {t.contact.route}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-border rounded-md overflow-hidden border border-border">
              {rows.map((r, i) => {
                const Icon = r.icon;
                const inner = (
                  <>
                    <span className="grid place-items-center h-11 w-11 rounded-md bg-ibk/10 text-ibk">
                      <Icon size={18} />
                    </span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mt-4">{r.label}</span>
                    <span className="block text-lg text-foreground mt-1 leading-snug">{r.value}</span>
                  </>
                );
                return (
                  <Reveal key={i} delay={i * 0.06}>
                    {r.href ? (
                      <a
                        href={r.href}
                        target={r.external ? "_blank" : undefined}
                        rel={r.external ? "noopener noreferrer" : undefined}
                        data-testid={r.tid}
                        className="group block h-full bg-[hsl(var(--card))] p-8 transition-colors duration-300 hover:bg-[hsl(var(--muted))]/60"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div data-testid={r.tid} className="h-full bg-[hsl(var(--card))] p-8">{inner}</div>
                    )}
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
